import { useAuth } from "@clerk/clerk-react";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";

import { fetcherAuth } from "utils/api";

export const ASSISTANT_AUTO_INTRO_SOURCE = "Auto Intro";

export type AssistantEntityType = "campaign" | "template";

export type ChipMetadata = {
  entity_type: AssistantEntityType;
  entity_id: number;
};

export type SuggestedPrompt = {
  label: string;
  prompt: string;
  metadata?: ChipMetadata;
};

export type PromptPage =
  | "home"
  | "campaigns"
  | "templates"
  | "inbox"
  | "discover"
  | "metrics_sidebar";

export type AssistantEntityContext = {
  entityType: AssistantEntityType;
  entityId: number;
};

export function useSuggestedPrompts(
  page: PromptPage | null,
  refreshKey: number = 0,
  entity?: AssistantEntityContext | null,
): { prompts: SuggestedPrompt[]; loading: boolean } {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const [prompts, setPrompts] = useState<SuggestedPrompt[]>([]);
  const [loading, setLoading] = useState(false);

  const entityType = entity?.entityType ?? null;
  const entityId = entity?.entityId ?? null;

  useEffect(() => {
    if (!currentOrg?.id || !page) {
      setPrompts([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setPrompts([]);
    setLoading(true);
    (async () => {
      try {
        const params = new URLSearchParams({ page });
        if (entityType && entityId !== null) {
          params.set("entity_type", entityType);
          params.set("entity_id", String(entityId));
        }
        const data = await fetcherAuth(
          getToken,
          `/api/organization/${
            currentOrg.id
          }/assistant/suggested-prompts?${params.toString()}`,
        );
        if (!cancelled) setPrompts(data?.prompts || []);
      } catch {
        if (!cancelled) setPrompts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentOrg?.id, page, refreshKey, entityType, entityId, getToken]);

  return { prompts, loading };
}
